"use client";

import { LayoutGridIcon } from "@/app/components/icons/LayoutGridIcon";
import { LayoutListIcon } from "@/app/components/icons/LayoutListIcon";
import { useT } from "@/hooks/useT";

export type ViewMode = "grid" | "list";

interface ViewModeToggleProps {
  viewMode: ViewMode;
  onChange: (mode: ViewMode) => void;
  /** Allows callers to layer responsive utilities like `lg:hidden`. */
  className?: string;
}

/**
 * 2-button grid/list switcher used in the markets toolbar.
 *
 * Extracted out of MarketsPageContent.tsx where the same JSX block was
 * duplicated four times (desktop, mobile, watchlist, breaking) with only
 * tiny styling tweaks. Visibility gates (viewModeLoaded, category !==
 * "sports", etc.) stay in the parent so the component itself is dumb.
 */
export function ViewModeToggle({ viewMode, onChange, className = "" }: ViewModeToggleProps) {
  const t = useT();
  return (
    <div className={`flex gap-0.5 bg-black/[0.03] dark:bg-white/[0.04] rounded-[5px] p-0.5 flex-shrink-0 ${className}`}>
      <button
        onClick={() => onChange("grid")}
        className={`p-1.5 rounded-[3px] transition-colors ${
          viewMode === "grid"
            ? "bg-gray-900 dark:bg-white text-white dark:text-black"
            : "text-gray-500 dark:text-gray-400 hover:bg-black/[0.06] dark:hover:bg-white/[0.08]"
        }`}
        title={t("viewMode.grid")}
        aria-label={t("viewMode.grid")}
      >
        <LayoutGridIcon size={14} isHovered={false} />
      </button>
      <button
        onClick={() => onChange("list")}
        className={`p-1.5 rounded-[3px] transition-colors ${
          viewMode === "list"
            ? "bg-gray-900 dark:bg-white text-white dark:text-black"
            : "text-gray-500 dark:text-gray-400 hover:bg-black/[0.06] dark:hover:bg-white/[0.08]"
        }`}
        title={t("viewMode.list")}
        aria-label={t("viewMode.list")}
      >
        <LayoutListIcon size={14} isHovered={false} />
      </button>
    </div>
  );
}
